import {isAfter, startOfDay, startOfToday} from 'date-fns';
import {
  Button,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {HStack, VStack, Show} from 'platform/foundation';
import {z} from 'zod';

import {isEmpty, isNil, isNotNil} from 'ramda';

import {
  PatchNoteItemPurchaseDateBulkRequest,
  usePatchNoteItemPurchaseDateBulkMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError, logError} from '@dms/shared';

import {
  getApiDateTimeString,
  Nullish,
  parseDate,
  RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

const SetPurchaseDateFormValuesSchema = z.object({
  mode: z.enum(['clear', 'select']),
  purchaseDate: z.date().nullable(),
});

type SetPurchaseDateFormType = z.infer<typeof SetPurchaseDateFormValuesSchema>;

const isDateInFuture = (date: Date) => isAfter(startOfDay(date), startOfToday());

// In "select" mode the user must actually pick a purchase date.
const isPurchaseDateProvidedWhenSelected = (values: SetPurchaseDateFormType) =>
  values.mode !== 'select' || isNotNil(values.purchaseDate);

// The picker blocks future days via maxDate, but a manually typed date can bypass it.
const isPurchaseDateNotInFuture = (values: SetPurchaseDateFormType) =>
  values.mode !== 'select' || isNil(values.purchaseDate) || !isDateInFuture(values.purchaseDate);

const SetPurchaseDateFormSchema = SetPurchaseDateFormValuesSchema.refine(
  isPurchaseDateProvidedWhenSelected,
  {
    path: ['purchaseDate'],
    message: i18n.t('errors.field_is_required'),
  }
).refine(isPurchaseDateNotInFuture, {
  path: ['purchaseDate'],
  message: i18n.t('errors.date_cannot_be_in_future'),
});

export interface SetPurchaseDateDialogProps extends RequiredTestIdProps {
  headerId: string;
  itemIds: string[];
  headerType: PatchNoteItemPurchaseDateBulkRequest['headerType'];
  initialPurchaseDate: string | Nullish;
  onSaved: VoidFunction;
}

export function SetPurchaseDateDialog(props: SetPurchaseDateDialogProps) {
  const [patchPurchaseDate, {isLoading}] = usePatchNoteItemPurchaseDateBulkMutation();

  const defaultValues: Partial<SetPurchaseDateFormType> = props.initialPurchaseDate
    ? {mode: 'select', purchaseDate: parseDate(props.initialPurchaseDate)}
    : {mode: 'clear', purchaseDate: null};

  const handleSubmit: FormSubmitHandler<SetPurchaseDateFormType> = async (formValues) => {
    if (isEmpty(props.itemIds)) {
      return logError('SetPurchaseDateDialog: no item to update');
    }

    const purchaseDate =
      formValues.mode === 'clear' || isNil(formValues.purchaseDate)
        ? null
        : getApiDateTimeString(formValues.purchaseDate);

    await patchPurchaseDate({
      headerId: props.headerId,
      itemId: props.itemIds,
      purchaseDate,
      headerType: props.headerType,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        props.onSaved();
        closeCurrentDialog();
      })
      .catch(handleApiError);
  };

  const modeOptions = [
    {value: 'clear', label: i18n.t('entity.warehouse.labels.clearPurchaseDate')},
    {value: 'select', label: i18n.t('entity.warehouse.labels.selectPurchaseDate')},
  ];

  const todayDate = startOfToday();

  return (
    <Form<SetPurchaseDateFormType>
      experimentalZodSchema={SetPurchaseDateFormSchema}
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
    >
      {(control, formApi) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            type="radio"
            name="mode"
            direction="column"
            spacing={4}
            options={modeOptions}
            data-testid={suffixTestId('inputs.mode', props)}
          />
          <Show when={formApi.watch('mode') === 'select'}>
            <FormField
              control={control}
              type="date"
              name="purchaseDate"
              maxDate={todayDate}
              data-testid={suffixTestId('inputs.purchaseDate', props)}
            />
          </Show>
          <HStack spacing={2} justify="flex-end">
            <Button
              title={i18n.t('general.actions.discard')}
              variant="secondary"
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('actions.discard', props)}
            />
            <FormButton
              control={control}
              type="submit"
              title={i18n.t('general.actions.confirm')}
              variant="primary"
              isLoading={isLoading}
              data-testid={suffixTestId('actions.confirm', props)}
            />
          </HStack>
        </VStack>
      )}
    </Form>
  );
}
